import { styled, Hidden } from '@ori-ui/mui';
import type { StaticApplicationsFooterBffFooterDto } from '@ori/static-api-client-react';
import { getTenantApplicationsFooter, useApiClient } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useEffect, useMemo, useState, useCallback } from 'react';

import {
  Content,
  ContentAllPages,
  ContentFrontPage,
  FollowUs,
  LanguageSelector,
  MarketExceptions,
  MobileApps,
  PaymentMethods,
  SiteLinks,
  Subscribe,
} from '../components';
import type { FooterContext } from '../contexts';
import { FooterProvider } from '../contexts';
import { useAppConfig } from '../hooks';
import { useReopenCookiePreferences } from '../hooks/useReopenCookiePreferences';
import { FooterAppSkeleton } from './FooterAppSkeleton';
import { FourthColumn } from './FourthColumn';
import { Highlight } from './Highlight';
import { HighlightGrid } from './HighlightGrid';

const useIsMobileOffice = () => {
  const [isMobileOffice, setIsMobileOffice] = useState(false);

  useEffect(() => {
    const mobileOfficeClassInHtml = 'page-mypages';

    if (document.getElementsByTagName('html')[0]?.classList.contains(mobileOfficeClassInHtml)) {
      setIsMobileOffice(true);
    }
  }, []);

  return isMobileOffice;
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginTop: theme.spacing(6),
  background: theme.palette.common.white,
}));

export const FooterContent: FC = () => {
  const { getTestId } = useTestId();
  const staticClient = useApiClient();
  const { tenant, currentLanguage } = useAppConfig();
  const [data, setData] = useState<StaticApplicationsFooterBffFooterDto>();
  useReopenCookiePreferences();
  const isMobileOffice = useIsMobileOffice();

  const getDataFromStatic = useCallback(async () => {
    const { data: footerData } = await getTenantApplicationsFooter(staticClient, tenant, {
      lang: currentLanguage,
    });

    return footerData;
  }, [currentLanguage, staticClient, tenant]);

  useEffect(() => {
    void getDataFromStatic().then((r) => {
      setData(r.data);
    });
  }, [getDataFromStatic]);

  const footerProviderValue = useMemo(
    (): FooterContext => ({
      data,
      isMobileOffice,
    }),
    [data, isMobileOffice],
  );

  if (!data) {
    return <FooterAppSkeleton />;
  }

  return (
    <FooterProvider value={footerProviderValue}>
      <Root data-testid={getTestId('root')}>
        <FollowUs />
        <Subscribe />
        <Highlight>
          <Hidden mdUp />
          <HighlightGrid isMobileOffice={isMobileOffice}>
            <SiteLinks />
            <FourthColumn>
              <MobileApps />
            </FourthColumn>
          </HighlightGrid>
        </Highlight>
        <Hidden mdUp>
          <MobileApps />
        </Hidden>
        <ContentFrontPage />
        <ContentAllPages />
        <MarketExceptions />
        <Content />
        <LanguageSelector />
        <PaymentMethods />
      </Root>
    </FooterProvider>
  );
};
